import { useEffect, useState } from 'react';
import { useAiImage } from './use-open-ai.js';
import { isFunction } from '#lib/utils/function.js';
import toast from 'react-hot-toast';
import { isHttpUrl } from '#components/images/helpers.js';
import { useExternalImage } from './api/image-service.js';
import { checkImageFormat } from '#helpers/helpers.js';

export function useImagePanel(props) {
    const {
        file,
        defaultState = "selecting",
        coords,
        handleChangePreview,
        fetchImagesFromLibrary,
        isOpen,
        onClose,
        clearAll
    } = props;

    // Stati per la getsione del file in modifica
    const [state, setState] = useState(defaultState)
    const [editingFile, setEditingFile] = useState(file);
    const [isError, setIsError] = useState(false)
    const [initCoords, setInitCoords] = useState(coords);
    const [isEdited, setIsEdited] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setEditingFile(file);
        setState(defaultState);
    }, [isOpen]);

    useEffect(() => {
        setInitCoords(coords);
    }, [coords]);

    // Stati per la gestione del pannello
    const [optionId, setOptionId] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [hasError, setHasError] = useState(false);

    // Handler per la modifica del file
    const handleAiImage = useAiImage({
        onSuccess: async (res) => {
            const file = new Image();
            file.src = res.url;
            if (optionId === "D") {
                setState("editing");
                setEditingFile(file);
            }
            setIsEdited(true);
            setIsLoading(false);
        },
        onError: () => {
            setIsError(true);
            setIsLoading(false);
        }
    });

    // Handler per il caricamento di immagini esterne su s3 temp
    const handleFetchExternalImage = useExternalImage({
        onSuccess: async (res) => {
            const file = new Image();
            file.src = res.url;
            if (optionId === "E" || optionId === "L") {
                setState("editing");
                setEditingFile(file);
            }
            setIsEdited(true);
            setIsLoading(false);
        },
        onError: () => {
            setIsError(true);
            setIsLoading(false);
        },
    })


    const handleUpdateFiles = (e) => {
        const { target: { files } } = e
        // Se stiamo caricando immagini da locale si controlla che siano effettivamente immagini
        if (!files || !files.length) {
            setEditingFile(null);
            setState("selecting");
            return;
        }
        if (!checkImageFormat(files[0])) {
            setEditingFile(null);
            setState("selecting");
            return
        }
        // setUploadEvent(e);
        setIsEdited(true);
        setEditingFile(files[0]);
        setState("editing");
    };

    const handleGenerateImage = async () => {
        setInitCoords({ x: 0, y: 0 });
        if (!inputValue) {
            setHasError(true);
            return;
        }
        if (optionId === 'L') {
            try {
                fetchImagesFromLibrary(inputValue, 1);
                setIsEdited(true);
            } catch (error) {
                setIsError(true);
            }
            return;
        }
        if (optionId === 'D') {
            handleAiImage.mutate({ prompt: inputValue });
            setIsLoading(true);
            setEditingFile(null);
        }
        if (optionId === 'E') {
            if (!isHttpUrl(inputValue)) {
                setHasError(true);
                return toast.error(_t.errors.invalidImageFormat());
            }
            handleFetchExternalImage.mutate({ url: inputValue });
            setIsLoading(true);
            setEditingFile(null);
        };
    };

    // Handler per la gestione del pannello
    const handleChangePanel = (id) => {
        setOptionId(id);
        if (id === "U") return;
        setState("uploading");
        setHasError(false);
        setEditingFile(null);
        setInputValue('');
    };

    const handleChangeText = (e) => {
        setInputValue(e.target.value);
        if (e.target?.value.length > 0) setHasError(false);
    };

    const handleSelectLibraryImage = (selectedImages) => {
        if (!selectedImages.length) {
            toast.error('Please select an image');
            return;
        }
        // Se stiamo caricando immagini da una libreria esterna
        handleFetchExternalImage.mutate({ url: selectedImages[0].src });
        setState("editing");
        setIsLoading(true);
        setEditingFile(null);
    };

    const handleDeleteEditingFile = () => {
        setEditingFile(null)
    }

    const handleSubmit = () => {
        if (isFunction(handleChangePreview)) {
            handleChangePreview(editingFile, initCoords);
            handleClose();
        }
    };

    const handleClose = (discard) => {
        setOptionId('');
        clearAll();
        onClose({ discard });
    };

    const handleClear = () => {
        setState("selecting")
        clearAll();
        setOptionId('');
        setEditingFile(null);
        setIsError(false);
    };

    return {
        // Gestione file in modifica
        state,
        setState,
        isError,
        setIsError,
        isLoading,
        setIsLoading,
        editingFile,
        initCoords,
        isEdited,
        handleInitCoords: (val) => {
            setInitCoords(val);
            setIsEdited(true);
        },
        handleIsEdited: setIsEdited,
        handleUpdateFiles,
        handleGenerateImage,
        // Gestione pannello
        optionId,
        inputValue,
        hasError,
        handleOptionId: setOptionId,
        handleInputValue: setInputValue,
        handleHasError: setHasError,
        handleChangePanel,
        handleChangeText,
        handleSelectLibraryImage,
        handleDeleteEditingFile,
        handleSubmit,
        handleClose,
        handleClear
    };
}
